import React, { useState } from "react";
import {
  FiX,
  FiAlertCircle,
  FiImage,
  FiCheckCircle,
  FiUploadCloud,
} from "react-icons/fi";
import { MdCancel } from "react-icons/md";

import { makeRequestAuth, uploadFiles } from "../../_config/api";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import Spinner from "../../utils/Loader";

const ViewP2POrderModal = ({ isOpen, onClose, transaction, fetchOrders }) => {
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [hashInput, setHashInput] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !transaction) return null;

  // Status Badge Styling
  const getStatusStyle = (statusVal) => {
    switch (statusVal) {
      case "approve":
        return "bg-emerald-100 text-emerald-600 border-emerald-200";
      case "reject":
        return "bg-red-100 text-red-600 border-red-200";
      case "requested":
        return "bg-blue-100 text-blue-600 border-blue-200";
      case "pending":
        return "bg-yellow-100 text-yellow-600 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmitBuyRequest = async (e) => {
    e.preventDefault();
    if (!hashInput.trim()) {
      return showErrorToast("Transaction Hash is required");
    }

    try {
      setIsSubmitting(true);
      let imageUrl = "";

      // 1. Upload image if selected
      if (imageFile) {
        const uploadResult = await uploadFiles({
          files: [imageFile],
          endpoint: "/uploadImage",
          fieldName: "file",
          isMultiple: false,
        });

        if (uploadResult?.data?.imageUrl) {
          imageUrl = uploadResult.data.imageUrl;
        } else {
          showErrorToast("Image upload failed. Try again.");
          setIsSubmitting(false);
          return;
        }
      }

      // 2. Submit Buy Request
      const payload = {
        orderId: transaction?._id ?? "NA",
        hash: hashInput,
        image: imageUrl,
      };

      const response = await makeRequestAuth(
        "post",
        "/admin/saveBuyRequest",
        payload,
      );

      if (!response.error) {
        showSuccessToast("Buy request submitted successfully!");
        setShowApproveModal(false);
        setHashInput("");
        setImageFile(null);
        setImagePreview(null);

        // Refresh parent list and close main modal
        if (fetchOrders) fetchOrders();
        onClose();
      } else {
        showErrorToast(response.message || "Failed to submit request");
      }
    } catch (err) {
      console.error("Submit Buy Request Error:", err);
      showErrorToast(
        err.response?.data?.message || err.message || "Server error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleCancelSubmitBuyRequest = async (e) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      const payload = {
        orderId: transaction?._id ?? "NA",
      };

      const response = await makeRequestAuth(
        "post",
        "/admin/cancelBuyRequest",
        payload,
      );

      if (!response.error) {
        showSuccessToast("Buy request submitted successfully!");
        setShowApproveModal(false);
        setHashInput("");
        setImageFile(null);
        setImagePreview(null);

        // Refresh parent list and close main modal
        if (fetchOrders) fetchOrders();
        onClose();
      } else {
        showErrorToast(response.message || "Failed to submit request");
      }
    } catch (err) {
      console.error("Submit Buy Request Error:", err);
      showErrorToast(
        err.response?.data?.message || err.message || "Server error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
        <div className="bg-white dark:bg-[#1E2738] rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col border border-purple-300 dark:border-purple-600/50 animate-fadeIn max-h-[90vh]">
          {/* Modal Header */}
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-t-2xl">
            <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Transaction Details
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition p-1"
            >
              <FiX size={24} />
            </button>
          </div>

          {/* Modal Body (Scrollable) */}
          <div className="p-6 overflow-y-auto custom-scrollbar space-y-6">
            {/* Order Summary Card */}
            <div className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-xl border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                Order Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Order ID
                  </p>
                  <p
                    className="font-mono text-sm font-medium text-gray-900 dark:text-white truncate"
                    title={transaction?.orderId ?? "NA"}
                  >
                    {transaction?.orderId ?? "NA"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Type
                  </p>
                  <span
                    className={`text-xs font-bold px-2 py-1 rounded ${transaction.txType === "BUY" ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"}`}
                  >
                    {transaction.txType}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Amount ({transaction.walletType})
                  </p>
                  <p className="font-bold text-gray-900 dark:text-white text-base">
                    {transaction.amount.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Rate / Status
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900 dark:text-white">
                      ₹{transaction.rate}
                    </span>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase ${getStatusStyle(transaction.status)}`}
                    >
                      {transaction.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Hash Info */}
              {transaction.hash && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Transaction UTR
                  </p>
                  <p className="font-mono text-sm font-bold text-gray-900 dark:text-white break-all bg-white dark:bg-gray-700/50 p-2 rounded-lg border border-gray-100 dark:border-gray-600">
                    {transaction.hash}
                  </p>
                </div>
              )}
            </div>

            {/* Users Info Card */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex justify-between">
                  <span>Creator</span>
                  <span className="text-xs text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30 px-2 rounded-full flex items-center">
                    {transaction.creatorModel}
                  </span>
                </h3>
                {transaction.creator ? (
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-gray-500">Name:</span>{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {transaction.creator.name || "N/A"}
                      </span>
                    </p>
                    <p>
                      <span className="text-gray-500">Email:</span>{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {transaction.creator.email || "N/A"}
                      </span>
                    </p>
                    <p>
                      <span className="text-gray-500">Mobile:</span>{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {transaction.creator.mobile || "N/A"}
                      </span>
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Creator details not populated.
                  </p>
                )}
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                  Counterparty
                </h3>
                {transaction.counterParty ? (
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-gray-500">Name:</span>{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {transaction.counterParty.name || "N/A"}
                      </span>
                    </p>
                    <p>
                      <span className="text-gray-500">Email:</span>{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {transaction.counterParty.email || "N/A"}
                      </span>
                    </p>
                    <p>
                      <span className="text-gray-500">Mobile:</span>{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {transaction.counterParty.mobile || "N/A"}
                      </span>
                    </p>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center pb-6">
                    <p className="text-sm text-gray-500 italic">
                      No counterparty assigned yet.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* COMBINED: Payment Methods & Receipt Image */}
            {(transaction.paymentMethods?.length > 0 || transaction.image) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Payment Methods */}
                <div
                  className={`flex flex-col gap-3 ${!transaction.image ? "md:col-span-2 grid sm:grid-cols-2" : ""}`}
                >
                  {transaction.paymentMethods?.length > 0 && (
                    <h3
                      className={`text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 px-1 ${!transaction.image ? "sm:col-span-2" : ""}`}
                    >
                      Payment Methods
                    </h3>
                  )}

                  {transaction.paymentMethods?.map((method, idx) => (
                    <div
                      key={idx}
                      className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700 relative w-full"
                    >
                      {method.isPrimary && (
                        <span className="absolute top-3 right-3 text-[10px] font-bold bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                          PRIMARY
                        </span>
                      )}
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                        {method.methodType}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        {method.label || "No Label"}
                      </p>

                      <div className="space-y-1.5 border-t border-gray-200 dark:border-gray-700 pt-3">
                        {Object.entries(method.details || {}).map(
                          ([key, value]) => (
                            <div key={key} className="flex flex-col">
                              <span className="text-[10px] text-gray-500 uppercase tracking-wider">
                                {key}
                              </span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white break-all">
                                {String(value)}
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Image Preview */}
                {transaction.image && (
                  <div className="flex flex-col h-full">
                    <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 px-1 flex items-center gap-2">
                      <FiImage /> Attached Receipt
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700 flex-1 flex flex-col items-center justify-center relative group">
                      <a
                        href={transaction.image}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full h-full flex items-center justify-center"
                      >
                        <img
                          src={transaction.image}
                          alt="Receipt"
                          className="max-h-[200px] w-auto object-contain rounded-lg shadow-sm border border-gray-300 dark:border-gray-600 transition-opacity group-hover:opacity-80 bg-white"
                        />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Timestamps */}
            <div className="flex flex-col sm:flex-row justify-between text-xs text-gray-500 dark:text-gray-400 px-1 pt-2">
              <p>Created: {formatDate(transaction.createdAt)}</p>
              <p>Last Updated: {formatDate(transaction.updatedAt)}</p>
            </div>
          </div>

          {/* Modal Footer with conditional ACTION BUTTON */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl flex justify-end gap-3">
            {transaction.txType === "BUY" &&
              transaction.status === "requested" && (
                <button
                  onClick={() => setShowRejectConfirm(true)}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white font-medium transition shadow-lg flex items-center gap-2"
                >
                  <MdCancel /> Reject Buy Request
                </button>
              )}
            {transaction.txType === "BUY" &&
              transaction.status === "requested" && (
                <button
                  onClick={() => setShowApproveModal(true)}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium transition shadow-lg flex items-center gap-2"
                >
                  <FiCheckCircle /> Complete Buy Request
                </button>
              )}
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-800 dark:text-white font-medium transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* --- SECONDARY MODAL FOR SUBMITTING HASH AND IMAGE --- */}
      {showApproveModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-[#1E2738] rounded-2xl shadow-2xl w-full max-w-lg border border-emerald-300 dark:border-emerald-600/50">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Submit Payment Details
              </h2>
              <button
                onClick={() => setShowApproveModal(false)}
                className="text-gray-500 hover:text-red-500 transition p-1"
              >
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmitBuyRequest} className="p-6 space-y-5">
              <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 p-3 rounded-lg text-sm mb-2">
                Provide the transaction hash and upload the payment receipt to
                mark this buy request as complete.
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Transaction UTR <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={hashInput}
                  onChange={(e) => setHashInput(e.target.value)}
                  placeholder="Enter UTR"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none transition"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Payment Receipt (Optional)
                </label>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6 text-center bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    disabled={isSubmitting}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  {imagePreview ? (
                    <div className="flex flex-col items-center">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="h-24 w-auto object-contain rounded shadow-md mb-2 bg-white"
                      />
                      <span className="text-xs text-emerald-600 font-medium">
                        Click to replace image
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center text-gray-500">
                      <FiUploadCloud size={32} className="mb-2 opacity-50" />
                      <p className="font-medium text-sm">
                        Upload Receipt Image
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowApproveModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-medium shadow-lg transition disabled:opacity-50 flex justify-center items-center gap-2"
                >
                  {isSubmitting ? (
                    <Spinner className="w-5 h-5" />
                  ) : (
                    "Submit Details"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showRejectConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-[#1E2738] rounded-2xl shadow-xl w-full max-w-md border border-red-300 dark:border-red-600/50 animate-fadeIn">
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <FiAlertCircle className="text-red-500 text-xl" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Confirm Rejection
              </h3>
            </div>

            {/* Body */}
            <div className="p-6 text-sm text-gray-600 dark:text-gray-300">
              Are you sure you want to reject this buy request? This action
              cannot be undone.
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => setShowRejectConfirm(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium"
              >
                Cancel
              </button>

              <button
                onClick={(e) => {
                  setShowRejectConfirm(false);
                  handleCancelSubmitBuyRequest(e);
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-medium shadow-lg transition"
              >
                Yes, Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewP2POrderModal;
